import {
  ResponseDatetimeSubTypeEnum,
  ResponseEmployeeSubTypeEnum,
  ResponseTextSubTypeEnum,
  ResponseTypeEnum,
  TemplateNodeSchema,
} from '@ulysses-inc/harami_api_client'
import type { ComponentType, ReactNode, SVGProps } from 'react'
import DateSvg from 'src/assets/icons/date.svg?react'
import EmployeeSvg from 'src/assets/icons/employee.svg?react'
import FormulaSvg from 'src/assets/icons/formula.svg?react'
import ImageSvg from 'src/assets/icons/image.svg?react'
import MultipleChoiceSvg from 'src/assets/icons/multiple_choice_set.svg?react'
import NumberSvg from 'src/assets/icons/number.svg?react'
import SignatureSvg from 'src/assets/icons/signature.svg?react'
import TextSvg from 'src/assets/icons/text.svg?react'
import TimeMeasurementSvg from 'src/assets/icons/time_measurement.svg?react'
import { DEFAULT_DEVIATE_COMMENT } from 'src/features/templateEdit/common/types'

type ColumnType = {
  label: string
  Icon: ComponentType<SVGProps<SVGSVGElement>>
}

/**
 * 表の列に設定できる回答種別。
 * 回答を伴わない表示専用の INFORMATION、グリッドレイアウト専用の GRID_VARIABLE、
 * ラベルチェック(LABEL_CHECK)は列にできず、それ以外の回答種別が対象。
 */
const COLUMN_INPUT_TYPE = [
  ResponseTypeEnum.TEXT,
  ResponseTypeEnum.NUMBER,
  ResponseTypeEnum.DATETIME,
  ResponseTypeEnum.MULTIPLE_CHOICE,
  ResponseTypeEnum.RESPONSE_SET,
  ResponseTypeEnum.SIGNATURE,
  ResponseTypeEnum.RESULT_IMAGE,
  ResponseTypeEnum.EMPLOYEE,
  ResponseTypeEnum.FORMULA,
  ResponseTypeEnum.TIME_MEASUREMENT,
] as const satisfies readonly ResponseTypeEnum[]

export type ColumnInputType = (typeof COLUMN_INPUT_TYPE)[number]

/**
 * 列入力種別ごとのラベル/アイコン。`satisfies Record<ColumnInputType, …>`
 * で全種別の定義漏れをコンパイル時に矯正する(種別を増やすとここがエラーになる)。
 */
const COLUMN_INPUT_TYPE_TO_UI = {
  [ResponseTypeEnum.TEXT]: { label: 'テキスト', Icon: TextSvg },
  [ResponseTypeEnum.NUMBER]: { label: '数値', Icon: NumberSvg },
  [ResponseTypeEnum.DATETIME]: { label: '日時', Icon: DateSvg },
  [ResponseTypeEnum.MULTIPLE_CHOICE]: {
    label: '選択肢',
    Icon: MultipleChoiceSvg,
  },
  [ResponseTypeEnum.RESPONSE_SET]: {
    label: 'セット選択肢',
    Icon: MultipleChoiceSvg,
  },
  [ResponseTypeEnum.SIGNATURE]: { label: '署名', Icon: SignatureSvg },
  [ResponseTypeEnum.RESULT_IMAGE]: { label: '写真撮影', Icon: ImageSvg },
  [ResponseTypeEnum.EMPLOYEE]: { label: '従業員', Icon: EmployeeSvg },
  [ResponseTypeEnum.FORMULA]: { label: '計算式', Icon: FormulaSvg },
  [ResponseTypeEnum.TIME_MEASUREMENT]: {
    label: '時間計測',
    Icon: TimeMeasurementSvg,
  },
} satisfies Record<ColumnInputType, ColumnType>

export const renderColumnTypeIcon = (
  responseType: ColumnInputType,
  size = 24,
): ReactNode => {
  const Icon = COLUMN_INPUT_TYPE_TO_UI[responseType].Icon
  return <Icon width={size} height={size} />
}

export const isColumnInputType = (
  responseType: ResponseTypeEnum,
): responseType is ColumnInputType => {
  return COLUMN_INPUT_TYPE.includes(responseType as ColumnInputType)
}

/** 列種別の表示(ラベル/アイコン)を得る。選択肢/セット選択肢も含む全種別に対応。 */
export const getColumnTypeUi = (responseType: ColumnInputType): ColumnType =>
  COLUMN_INPUT_TYPE_TO_UI[responseType]

/**
 * 「入力フォーム」型パレットに並べる列種別。
 * 選択肢/セット選択肢は型ではなく専用の選択肢パネルで選ぶため、ここには含めない
 * (既存の質問編集 UI と同じ扱い)。
 * 並び順は通常質問の入力フォームメニュー(itemAppender/InputMenu.tsx の defaultItems)に揃える。
 */
export const COLUMN_FORM_INPUT_TYPES = [
  ResponseTypeEnum.NUMBER,
  ResponseTypeEnum.SIGNATURE,
  ResponseTypeEnum.TEXT,
  ResponseTypeEnum.RESULT_IMAGE,
  ResponseTypeEnum.DATETIME,
  ResponseTypeEnum.TIME_MEASUREMENT,
  ResponseTypeEnum.EMPLOYEE,
  ResponseTypeEnum.FORMULA,
] as const satisfies readonly ColumnInputType[]

export type ColumnFormInputType = (typeof COLUMN_FORM_INPUT_TYPES)[number]

/** 入力フォーム型パレットの表示用(アイコン+ラベル)。 */
export const COLUMN_FORM_INPUT_OPTIONS: ({
  type: ColumnFormInputType
} & ColumnType)[] = COLUMN_FORM_INPUT_TYPES.map(type => ({
  type,
  ...COLUMN_INPUT_TYPE_TO_UI[type],
}))

/**
 * 表の列に対する回答形式変更。
 * 回答形式に応じて sub-array をリセットしつつ、付随設定(scale / deviate など)も初期化する。
 * セル側は 保存時に同期され、列に揃えられる。
 *
 * NOTE: responseType ごとの初期 response 系フィールドの正は、通常質問の
 * 回答形式変更(question/QuestionBody.tsx の onChangeQuestionType)にある。
 * ここはその複製のため、内容は QuestionBody 側と一致させること。
 */
export const applyColumnQuestionTypeChange = (
  columnNode: TemplateNodeSchema,
  responseType: ColumnInputType,
): TemplateNodeSchema => {
  return {
    ...columnNode,
    question: {
      ...columnNode.question,
      scale: '',
      isShowInformationImages: 0,
      informationImages: [],
      responseType,
      responseMultipleChoices: [],
      responseMultipleChoiceSets: [],
      responseNumbers: [],
      responseTexts:
        responseType === ResponseTypeEnum.TEXT
          ? [{ subType: ResponseTextSubTypeEnum.SINGLE_LINE }]
          : [],
      responseDatetimes:
        responseType === ResponseTypeEnum.DATETIME
          ? [{ subType: ResponseDatetimeSubTypeEnum.DATETIME }]
          : [],
      responseTimeMeasurements: [],
      responseInformations: [],
      responseEmployees:
        responseType === ResponseTypeEnum.EMPLOYEE
          ? [{ subType: ResponseEmployeeSubTypeEnum.NAME, isMultiple: 0 }]
          : [],
      responseFormulas: [],
      responseGridVariables: [],
      responseLabelChecks: [],
      responseURLs: [],
      answerOption: undefined,
      deviateComment: DEFAULT_DEVIATE_COMMENT,
      deviateNotify: undefined,
    },
  }
}
